import React, { useState, useEffect } from "react";
import { Check, XCircle, ClipboardList, Users } from "lucide-react";
import {
  approveTask,
  rejectTask,
  getTaskDetailsByEmployeeDate,
  getCurrentApprover,
  getSubordinateTasks,
} from "../../../../../api/hrm/tasks/task_info";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../../../components/ConfirmDialog";
import dayjs from "../../../../../utils/dayjs";
import {
  TaskPopupFrame,
  PopupSectionCard,
  PopupActionButton,
  PopupStatusBadge,
  PopupEmployeeInfoCard,
  PopupPromptSheet,
  resolveTaskTypeLabel,
} from "./PopupUiKit";

export default function TaskPopupWaiting({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  branchName,
  workDay,
  taskList = [],
  onUpdated,
}) {
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [taskDetails, setTaskDetails] = useState([]);
  const [subordinateTasks, setSubordinateTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedTaskIndex, setSelectedTaskIndex] = useState(null);
  const [approvingIndex, setApprovingIndex] = useState(null);
  const [approvableTasks, setApprovableTasks] = useState({});

  // 🔍 Lấy chi tiết công việc + công việc cấp dưới
  const fetchTaskDetails = async () => {
    try {
      if (!employeeId || !workDay) return;

      // 1️⃣ Lấy công việc của nhân viên đang được duyệt
      const data = await getTaskDetailsByEmployeeDate(employeeId, workDay);
      setTaskDetails(data || []);

      // ✅ 1.1 Kiểm tra người duyệt hiện tại (multi-level) - gộp vào đây
      const approverResults = {};
      for (const work of data || []) {
        if (!work?.id) continue;
        try {
          const res = await getCurrentApprover(work.id);
          const approvers = res?.approvers || [];
          const isCurrent = approvers.some(
            (a) => a.id === currentUser.id
          );
          approverResults[work.id] = isCurrent;
        } catch {
          approverResults[work.id] = false;
        }
      }
      setApprovableTasks(approverResults);

      // 2️⃣ Lấy position của nhân viên đang được duyệt
      // Ưu tiên `position` (id/chuẩn rule) trước `employee_position` (text hiển thị)
      let empPosition = null;
      if (data && Array.isArray(data) && data.length > 0) {
        empPosition = data[0]?.position || data[0]?.employee_position || null;
      }

      // 3️⃣ Lấy công việc cấp dưới
      if (empPosition) {
        const subs = await getSubordinateTasks(workDay, empPosition);
        setSubordinateTasks(subs || []);
      } else {
        setSubordinateTasks([]);
      }
    } catch (err) {
      console.error("❌ Lỗi lấy dữ liệu công việc:", err);
      setTaskDetails([]);
      setSubordinateTasks([]);
    }
  };

  // 🧩 Gọi 1 lần khi popup mở
  useEffect(() => {
    if (isOpen && employeeId && workDay) fetchTaskDetails();
  }, [isOpen, employeeId, workDay]);

  // ✅ Phê duyệt
  const handleApprove = async (index) => {
    try {
      const work = taskDetails[index];
      if (!work) return;
      setApprovingIndex(index);
      setLoading(true);

      const res = await approveTask(work.id, {
        approved_by: currentUser?.id,
        note: `Duyệt công việc: ${work.taskname}`,
      });

      showSuccessToast(res.message || "Đã duyệt công việc!");
      await fetchTaskDetails();
      onUpdated?.();
    } catch (err) {
      console.error("❌ Lỗi duyệt công việc:", err);
      showErrorToast("Không thể duyệt công việc");
    } finally {
      setApprovingIndex(null);
      setLoading(false);
    }
  };

  // ❌ Từ chối
  const handleReject = (index) => {
    setSelectedTaskIndex(index);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    try {
      setLoading(true);
      const work = taskDetails[selectedTaskIndex];
      if (!work) return;

      const res = await rejectTask(work.id, {
        rejected_by: currentUser?.id,
        reason: rejectReason || "Không duyệt",
      });

      showSuccessToast(res.message || "Đã từ chối công việc");
      await fetchTaskDetails();
      onUpdated?.();
      setShowRejectModal(false);
      setRejectReason("");
    } catch (err) {
      console.error("❌ Lỗi từ chối công việc:", err);
      showErrorToast("Không thể từ chối công việc");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !employeeId) return null;

  const typeFromLoaded = resolveTaskTypeLabel(taskDetails);
  const typeFromCard = resolveTaskTypeLabel(taskList);
  const taskTypeLabel =
    typeFromLoaded !== "—" ? typeFromLoaded : typeFromCard;
  const branchDisplay =
    branchName ||
    taskDetails?.[0]?.branch_name ||
    "—";

  return (
    <>
      <TaskPopupFrame
        isOpen={isOpen}
        onClose={onClose}
        maxWidthClass="max-w-5xl"
        titleIcon={<ClipboardList className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />}
        title="Duyệt"
        titleHighlight="công việc"
        subtitle="Phê duyệt đăng ký / giao việc trong ngày"
      >
        <PopupEmployeeInfoCard
          branchName={branchDisplay}
          employeeName={employeeName}
          workDay={
            dayjs(workDay).isValid()
              ? dayjs(workDay).format("DD/MM/YYYY")
              : "—"
          }
          taskTypeLabel={taskTypeLabel}
        />

                {/* Công việc chính */}
                {!Array.isArray(taskDetails) || taskDetails.length === 0 ? (
                  <p className="text-gray-500 text-sm italic bg-white rounded-2xl border border-gray-300 p-4">
                    Không có công việc nào trong ngày này.
                  </p>
                ) : (
                  taskDetails.map((work, i) => (
                    <PopupSectionCard key={work.id || i}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {i + 1}. {work.taskname || "Không có tiêu đề"}
                          </p>
                          {work.note && (
                            <p className="text-gray-500 text-sm mt-1 italic">{work.note}</p>
                          )}
                        </div>
                        <PopupStatusBadge status={work.status} />
                      </div>

                      {/* Nút hành động */}
                      {approvableTasks[work.id] && work.status === "waiting" && (
                        <div className="flex justify-end mt-4 gap-3">
                          <PopupActionButton
                            onClick={() => handleReject(i)}
                            disabled={loading}
                            variant="danger-outline"
                          >
                            <XCircle size={16} className="stroke-[2]" />
                            Không duyệt
                          </PopupActionButton>
                          <PopupActionButton
                            onClick={() => handleApprove(i)}
                            disabled={loading || approvingIndex === i}
                          >
                            <Check size={16} className="stroke-[2]" />
                            {approvingIndex === i
                              ? "Đang duyệt..."
                              : "Duyệt"}
                          </PopupActionButton>
                        </div>
                      )}
                    </PopupSectionCard>
                  ))
                )}

                {/* Bảng công việc cấp dưới */}
                {subordinateTasks.length > 0 && (
                  <div className="mt-2 border-t border-gray-200 pt-6">
                    <h3 className="font-black uppercase tracking-wide flex items-center gap-2 text-gray-800 mb-3 text-sm">
                      <Users className="w-5 h-5 text-red-600" />
                      Công việc của nhân viên cấp dưới
                    </h3>

                    <div className="overflow-x-auto rounded-2xl border border-gray-300 bg-white shadow-sm">
                      <table className="min-w-full text-sm text-gray-700">
                        <thead className="bg-gray-50 text-gray-700 font-medium">
                          <tr>
                            <th className="px-4 py-3 text-left w-32 text-[11px] uppercase font-black text-gray-400">Trạng thái</th>
                            <th className="px-4 py-3 text-left text-[11px] uppercase font-black text-gray-400">Nhân viên</th>
                            <th className="px-4 py-3 text-left text-[11px] uppercase font-black text-gray-400">Nội dung công việc</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subordinateTasks.map((t, i) => (
                            <tr
                              key={t.id}
                              className={`border-t border-gray-300 hover:bg-red-50/30 ${
                                i % 2 === 0 ? "bg-white" : "bg-gray-50/40"
                              }`}
                            >
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-block text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider border ${
                                    t.status === "approved"
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                      : t.status === "rejected"
                                      ? "bg-red-50 text-red-700 border-red-100"
                                      : "bg-amber-50 text-amber-700 border-amber-100"
                                  }`}
                                >
                                  {t.status === "approved"
                                    ? "Đã duyệt"
                                    : t.status === "rejected"
                                    ? "Không duyệt"
                                    : "Chờ duyệt"}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-col">
                                  <span className="font-medium text-gray-800">{t.employee_name}</span>
                                  <span className="text-gray-500 text-xs">{t.employee_position}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-col">
                                  <span className="text-gray-800">{t.taskname}</span>
                                  {t.note && (
                                    <span className="text-gray-500 text-xs italic mt-1">
                                      Ghi chú: {t.note}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
      </TaskPopupFrame>

      <PopupPromptSheet
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Lý do không duyệt"
        value={rejectReason}
        onChange={setRejectReason}
        onConfirm={handleRejectConfirm}
        loading={loading}
        placeholder="Nhập lý do..."
      />
    </>
  );
}
